/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Link,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import EditableList from '../../../components/EditableList';
import { AccountApi, GlobalApi } from '@Apis';
import AlertModal from '../../../components/Common/Modal/AlertModal';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import common from '../../../utils/common';
import CSpinner from '../../../components/CSpinner';
import { ValidProps } from '../../../types';
import { validateDomain } from '../../../utils/validate';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import config from 'src/config';
import ImportModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ImportModal';

const ApprovedHeaderFields = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { widthEditablePermission } = usePermissionStore();
  const [visible, setVisible] = useState<boolean>(false);
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = React.useState<{
    title: string;
    content: JSX.Element;
  }>();
  const [items, setItems] = useState<string[]>([]);
  const [needSyncDomainsInDB, setNeedSyncDomainsInDB] = useState<string[]>();
  const confirmSyncExistingDomains = async () => {
    try {
      if (needSyncDomainsInDB?.length !== 0) {
        await GlobalApi.addInternalDomains(
          Array.from(new Set(needSyncDomainsInDB)) || []
        );
      }
      onClose();
      setAlertMessage(undefined);
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      await loadData();
    }
  };

  const validates: ValidProps[] = [
    {
      validMethod: validateDomain,
      errMsg: t('common.error.invalidDomain'),
    },
    {
      validMethod: (value: string) => {
        return !items.includes(value);
      },
      errMsg: t('common.error.duplicateDomain'),
    },
  ];

  const diffDomains = async () => {
    try {
      setLoading(true);
      const domains: string[] = (await AccountApi.getAllDomains()).data;
      const diff = Array.from(
        new Set(domains.filter((domain) => !items?.includes(domain)))
      );
      console.log(diff);
      setNeedSyncDomainsInDB(diff);
      onOpen();
      setAlertMessage({
        title: t(
          'globalSetting.internalDomains.syncExistingDomains.alertTitle'
        ),
        content: (
          <Box>
            <Text mb={8}>
              {t(
                'globalSetting.internalDomains.syncExistingDomains.alertContentOne'
              )}
              {diff.length}
            </Text>
            {diff.length !== 0 && (
              <Text>
                {t(
                  'globalSetting.internalDomains.syncExistingDomains.alertContentTwo'
                )}
              </Text>
            )}
            {diff.map((item, index) => (
              <Text key={index}>{item}</Text>
            ))}
          </Box>
        ),
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  async function loadData() {
    try {
      setLoading(true);
      const internalDomains: { internalDomains: string[] } = (
        await GlobalApi.getInternalDomains()
      ).data;
      setItems(internalDomains.internalDomains);
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, []);

  const onDelete = async (value: string) => {
    try {
      await GlobalApi.deleteInternalDomains([value]);
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      await loadData();
    }
  };
  const onAdd = async (value: string) => {
    try {
      const exist = items.includes(value);
      if (!exist) {
        await GlobalApi.addInternalDomains([value]);
      }
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      await loadData();
    }
  };

  const onExport = () => {
    let content = '';
    if (!items) return;
    items.forEach(function (val) {
      content += val + '\r\n';
    });
    common.exportFile(
      `${t('globalSetting.internalDomains.tab.title')}.txt`,
      content,
      'text/plain'
    );
  };

  const onImport = () => {
    setImportModalVisible(true);
  };

  const handleImport = async (content: string[]) => {
    const differ = Array.from(
      new Set(content.filter((item) => !items.includes(item)))
    );
    const invalidDomains = differ.filter((item) => !validateDomain(item));
    if (invalidDomains.length > 0) {
      setVisible(true);
      setAlertMessage({
        title: t('common.error.invalidDomain'),
        content: (
          <Box>
            {invalidDomains.map((item) => (
              <Text key={item}>{item}</Text>
            ))}
          </Box>
        ),
      });
    } else {
      try {
        if (differ.length !== 0) {
          await GlobalApi.addInternalDomains(differ);
          setItems(Array.from(new Set(items.concat(content))));
        }
        setImportModalVisible(false);
      } catch (e) {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      } finally {
        await loadData();
      }
    }
  };
  return (
    <Box px="4x">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      <ImportModal
        isVisible={importModalVisible}
        onClose={() => {
          setImportModalVisible(false);
        }}
        onImport={handleImport}
      />
      <AlertModal
        visible={visible}
        onClose={() => setVisible(false)}
        title={alertMessage?.title || ''}
        content={alertMessage?.content || ''}
        onConfirm={() => setVisible(false)}
      ></AlertModal>
      <AlertModal
        visible={isOpen}
        onClose={onClose}
        title={alertMessage?.title || ''}
        content={alertMessage?.content || ''}
        onConfirm={confirmSyncExistingDomains}
        confirmButtonName={t('common.button.ok')}
      ></AlertModal>
      <Flex m="16px 0px">
        <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
          {t('globalSetting.internalDomains.tab.title')}
        </Text>
      </Flex>
      <Flex m="16px 0px">
        <Text
          color={colorStyle.color.secondary}
          lineHeight="lg"
          maxWidth={1000}
        >
          {t('globalSetting.internalDomains.description')}
        </Text>
      </Flex>
      <Flex m="16px 0px">
        <Text>
          <Space>{t('globalSetting.note.title')}</Space>
          <Space width="1x"></Space>
          <Space color={colorStyle.color.secondary}>
            {t('globalSetting.note.applyToAllOrganizations')}
          </Space>
        </Text>
      </Flex>
      <Flex alignItems="center">
        <EditableList
          onDelete={onDelete}
          onAdd={onAdd}
          onExport={onExport}
          onImport={onImport}
          disabled={
            !widthEditablePermission(PermissionNameEnum.administrator) ||
            items.length >=
              config.ui.globalSettings.limitation.internalDomainList
          }
          list={items?.map((item) => ({ name: item, value: item })) || []}
          limit={500}
          description={''}
          width={'800px'}
          size={10}
          placeholder={t('globalSetting.internalDomains.placeholder.content')}
          validates={validates}
        />
      </Flex>
      <Flex m="16px 0px">
        <Text>
          <Trans i18nKey="globalSetting.internalDomains.syncExistingDomains.link">
            <Link
              onClick={() => diffDomains()}
              disabled={
                !widthEditablePermission(PermissionNameEnum.administrator)
              }
            >
              placeholder
            </Link>
            placeholder
          </Trans>
        </Text>
      </Flex>
    </Box>
  );
};
export default ApprovedHeaderFields;
